import {
	HowToVideoModel,
	HowToVideoStatus,
	getVideoList,
	HowToVideosListWithCountersModel
} from "client/Api/Workspace/LiveInformation/HowTos/howToLiveInformation";
import { HttpRequestError } from "client/Api/httpClient";
import { InterestedParty, LiveInformationProvider, SuccessfulCacheItem } from "client_legacy/Workspace/LiveInformation/liveInformationProvider";
import { ensureNotNil } from "client/Misc/utils";

interface HowToLiveInformationProviderOptions {
	intervalInSeconds: number;
}

interface SuccessHowToListModel {
	videoStatus: HowToVideoStatus;
	status: "success";
	videos: HowToVideoModel[];
	unwatchedVideosCount: number;
	watchedVideosCount: number;
}

interface ErrorHowToListModel {
	videoStatus: HowToVideoStatus;
	status: "error";
}

export type HowToListModel = SuccessHowToListModel | ErrorHowToListModel;

export type HowToResourceId = "howtos";

type HowToInterests = {
	unwatched?: boolean;
	watched?: boolean;
};

type HowToReponseModelData = {
	unwatched?: HowToListModel;
	watched?: HowToListModel;
};

export interface HowToInterestedParty extends InterestedParty<number, HowToResourceId> {
	updateList(listModel: HowToListModel): void;
};

export class HowToLiveInformationProvider extends LiveInformationProvider<
	HowToLiveInformationProviderOptions,
	number,
	HowToResourceId,
	HowToInterests,
	HowToInterestedParty,
	HowToReponseModelData
>
{
	protected async getDataFromBackend(
		entries: readonly [HowToResourceId, HowToInterests][]
	): Promise<readonly [HowToResourceId, HowToReponseModelData][]> {
		// NOTE: currently there is only a static resource id, so we just take the first interests
		const [, interests] = entries[0];

		let unwatched: HowToListModel | undefined;
		if (interests.unwatched) {
			unwatched = await this.load(HowToVideoStatus.Unwatched);
		}

		let watched: HowToListModel | undefined;
		if (interests.watched) {
			watched = await this.load(HowToVideoStatus.Watched);
		}

		return entries.map(
			([resourceId, interests]) => {
				const response: HowToReponseModelData = {};
				if (interests.watched) {
					response.watched = watched;
				}

				if (interests.unwatched) {
					response.unwatched = unwatched;
				}

				return [resourceId, response];
			}
		);
	}

	protected updateCacheItem(
		cacheItem: SuccessfulCacheItem<HowToInterests, HowToReponseModelData>,
		response: [HowToInterests, HowToReponseModelData]
	): void {
		const [interests, model] = response;
		if (interests.watched) {
			cacheItem.retrievedInterests.watched = true;
			cacheItem.data.watched = model.watched;
		}

		if (interests.unwatched) {
			cacheItem.retrievedInterests.unwatched = true;
			cacheItem.data.unwatched = model.unwatched;
		}
	}

	protected informParty(
		party: HowToInterestedParty,
		cacheItem: [HowToInterests, HowToReponseModelData]
	): void {
		const [retrievedInterests, response] = cacheItem;
		const interestedIn: HowToInterests = this.getInterestsForEnsuredParty(party);

		if (interestedIn.watched && retrievedInterests.watched) {
			party.updateList(
				ensureNotNil(
					response.watched
				)
			);
		}

		if (interestedIn.unwatched && retrievedInterests.unwatched) {
			party.updateList(
				ensureNotNil(
					response.unwatched
				)
			);
		}
	}

	private async load(status: HowToVideoStatus): Promise<HowToListModel> {
		const videosListWithCounters: HowToVideosListWithCountersModel = await getVideoList({ status });
		try {
			return {
				status: "success",
				videoStatus: status,
				videos: videosListWithCounters.videos,
				unwatchedVideosCount: videosListWithCounters.unwatchedVideosCount,
				watchedVideosCount: videosListWithCounters.watchedVideosCount
			};
		} catch(e) {
			if (e instanceof HttpRequestError && e.statusCode === 403) {
				throw e;
			}

			return {
				videoStatus: status,
				status: "error"
			};
		}
	}
}
